import * as React from 'react';
import { ExportableFilters } from './Filters.interfaces';
import FiltersHeader from './FiltersHeader';
import FilterTab from './FilterTab';
import FilterSection from './FilterSection';
import styles from './Filters.styles';
import { DaysRangeListResponse, Filter, FilterGroup } from 'src/types/Scope';
import type { ContextFilterConfig } from 'configuration-schemas/confdefn';

export interface FilterSelection {
  id: string;
  filterDefnId: string;
}

export interface SelectionOverride {
  id: string;
  filterDefnId: string;
  value: boolean;
}

export interface Props {
  data: FilterGroup[];
  width?: number | string;
  height?: number | string;
  lockedNavigation: boolean;
  selectionOverrides: SelectionOverride[];
  selectionsChanged: boolean;
  hasFiltersSelected: boolean;
  allowFrom: string;
  allowTo: string;
  daysRangeList: DaysRangeListResponse;
  scopeStartMonth: string | null;
  contextFilterConfig?: ContextFilterConfig;
  loadingSectionId?: string | null;
  onFilterToggled(itemData: SelectionOverride): void;
  onFiltersCleared(): Promise<void>;
  onFilterTabCleared(tab: number): void;
  onFilterSectionCleared(sectionId: string, isOpen: boolean): void;
  onFiltersSubmit(): Promise<void>;
  onToggleSection(filter: Filter): void;
  onRangeFiltersUpdated?(id: string, labels: string[]): void;
}

export interface State {
  currentTab: number;
  currentSection: string[];
  filterSets: {
    appName: string;
    history: boolean;
  };
  filterSearchText: string;
}

export default class Filters extends React.Component<Props, State> implements ExportableFilters {
  state: State = {
    currentTab: 0,
    currentSection: [],
    filterSets: {
      appName: 'assortment',
      history: false,
    },
    filterSearchText: '',
  };

  constructor(props: Props) {
    super(props);
  }

  onSubmitFilters = (): Promise<void> => {
    this.setState({ ...this.state, currentSection: [] });
    return this.props.onFiltersSubmit();
  };

  onSelectTab = (tab: number) => {
    if (!this.props.lockedNavigation) {
      this.setState({
        // We need to force a reset of open section on every tab switch
        // This can probably change to just a string | undefined in the future
        // but I wasn't comfortable making more invasive changes
        currentSection: [],
        currentTab: tab,
        filterSearchText: '',
      });
    }
  };

  updateRangeFilters = (id: string, labels: string[]) => {
    const { onRangeFiltersUpdated } = this.props;
    if (onRangeFiltersUpdated) {
      onRangeFiltersUpdated(id, labels);
    }
  };

  toggleFilter = (itemData: SelectionOverride) => {
    this.props.onFilterToggled(itemData);
  };

  toggleSection = (newSection: string) => {
    if (!this.props.lockedNavigation) {
      const { currentSection, currentTab } = this.state;
      const updatedSection = currentSection;

      if (currentSection[currentTab] !== newSection) {
        updatedSection[currentTab] = newSection;
      } else {
        updatedSection[currentTab] = '';
      }

      const { data } = this.props;
      const currentFilters = data[currentTab].filters;
      const filter = currentFilters.find((x) => x.id === currentSection[currentTab]);
      if (filter) {
        this.props.onToggleSection(filter);
      }
      this.setState({
        currentSection: updatedSection,
      });
    }
  };

  clearAllFilters = (): Promise<void> => {
    this.setState({ ...this.state, currentSection: [] });
    return this.props.onFiltersCleared();
  };

  clearTabFilters = (tab: number) => {
    this.props.onFilterTabCleared(tab);
  };

  clearSectionFilters = (sectionId: string, isOpen: boolean) => {
    this.props.onFilterSectionCleared(sectionId, isOpen);
  };

  handleGlobalSearchChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({ filterSearchText: event.currentTarget.value });
  };

  render() {
    const {
      data,
      selectionsChanged,
      selectionOverrides = [],
      hasFiltersSelected,
      allowFrom,
      allowTo,
      daysRangeList,
      scopeStartMonth,
      contextFilterConfig,
      loadingSectionId,
    } = this.props;
    const { currentTab, currentSection, filterSearchText } = this.state;
    if (!data[currentTab]) return <div />;

    const tabs = data.map((x) => x.id).filter((t) => !contextFilterConfig?.groups?.blacklist?.includes(t));

    const currentFilters: Filter[] = data[currentTab].filters;
    const normalizedQuery = filterSearchText.trim().toLowerCase();

    const filteredFilters = currentFilters.filter((filter) => {
      if (normalizedQuery === '') return true;
      return filter.name.toLowerCase().includes(normalizedQuery);
    });

    return (
      <div className={styles.FiltersView}>
        <FiltersHeader
          onSubmitFilters={this.onSubmitFilters}
          onClearAllFilters={this.clearAllFilters}
          hasFiltersSelected={hasFiltersSelected}
          areFiltersChanged={selectionsChanged}
        />
        <div className="tabs" data-qa="filter-tab-container">
          {tabs.map((tab, index) => (
            <FilterTab
              key={`tab_${index}`}
              name={tab}
              index={index}
              selected={index === currentTab}
              onSelectTab={this.onSelectTab}
              clearTabSelections={this.clearTabFilters}
            />
          ))}
        </div>
        <div className="global-search" data-qa="filter-global-search">
          <div className="search">
            <i className="far fa-search" />
            <input
              className={styles.searchInput}
              type="text"
              value={filterSearchText}
              onChange={this.handleGlobalSearchChange}
              placeholder="Search filters"
            />
          </div>
        </div>
        <div className="group-container" data-qa="filter-section-container">
          {filteredFilters &&
            filteredFilters.map((filter, i) => {
              const { id, name, filterValues = [], inputType } = filter;

              const sectionProps = {
                selectionOverrides,
                id,
                inputType,
                name,
                open: currentSection[currentTab] === filter.id,
                filterValues,
                allowFrom,
                allowTo,
                daysRangeList,
                scopeStartMonth,
                isLoading: loadingSectionId === filter.id,
                onClearSection: this.clearSectionFilters,
                onToggleItem: this.toggleFilter,
                onToggleSection: this.toggleSection,
                onRangeFiltersUpdated: this.updateRangeFilters,
              };

              return <FilterSection key={'filter_section_' + i} {...sectionProps} />;
            })}
          {filteredFilters.length === 0 && (
            <div className="empty" data-qa="filter-search-empty">
              No filters match {filterSearchText}
            </div>
          )}
        </div>
      </div>
    );
  }
}
